/**
 * Offline Documents Service
 * 
 * Manages offline caching of documents for access without internet connection.
 * Documents are downloaded to local file system and metadata is cached in AsyncStorage.
 * Supports auto-download for active trips and storage management.
 */

import * as FileSystem from '@/lib/fileSystemLegacy';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { Document, DocumentType } from './documentVault';
import { getDocumentWithDownloadUrl } from './documentVault';
import { networkMonitor } from './networkMonitor';

const OFFLINE_DOCUMENTS_KEY = '@tripflow_offline_documents';
const OFFLINE_DOCUMENTS_DIR = `${FileSystem.documentDirectory}offline_documents/`;
const TRIP_DOCUMENTS_KEY = '@tripflow_trip_documents'; // Track which documents belong to which trips

// Helper to check if a trip is active (within 7 days)
function isWithin7Days(date: Date): boolean {
  const now = new Date();
  const tripDate = new Date(date);
  const diffTime = tripDate.getTime() - now.getTime();
  const diffDays = diffTime / (1000 * 60 * 60 * 24);
  return diffDays >= 0 && diffDays <= 7;
}

export type OfflineDocument = {
  documentId: string;
  name: string;
  type: string;
  localPath: string;
  downloadUrl: string;
  downloadedAt: number;
  size: number;
  expiresOn?: number | null;
  metadata?: Record<string, unknown>;
  /** MIME from vault (required for correct extension when filename has no suffix). */
  contentType?: string | null;
  /** Original `document.createdAt` (ms); avoids showing download time as "Created". */
  sourceCreatedAt?: number;
};

const VAULT_DOCUMENT_TYPES: DocumentType[] = [
  'passport',
  'visa',
  'driving_license',
  'health_record',
  'insurance',
  'other',
];

function offlineStoredMetadataHasValues(meta: Record<string, unknown> | undefined): boolean {
  if (!meta || typeof meta !== 'object') return false;
  return Object.values(meta).some((v) => v !== undefined && v !== null && v !== '');
}

/** Build vault `Document` rows from on-device offline copies (for list UI when Firestore is empty). */
export function mapOfflineDocumentsToVaultDocuments(
  offline: OfflineDocument[],
  userId: string,
): Document[] {
  return offline.map((d) => {
    const t = d.type as DocumentType;
    const type: DocumentType = VAULT_DOCUMENT_TYPES.includes(t) ? t : 'other';
    const metaCt =
      d.contentType ??
      (d.metadata && typeof (d.metadata as { contentType?: string }).contentType === 'string'
        ? (d.metadata as { contentType: string }).contentType
        : null);
    const rawMeta =
      offlineStoredMetadataHasValues(d.metadata as Record<string, unknown> | undefined)
        ? (deepDeserializeDates(d.metadata) as Document['metadata'])
        : undefined;
    return {
      id: d.documentId,
      name: d.name,
      type,
      entryType: 'upload',
      storagePath: d.localPath,
      size: d.size,
      contentType: metaCt,
      metadata: rawMeta,
      expiresOn: d.expiresOn != null ? new Date(d.expiresOn) : null,
      createdAt: new Date(d.sourceCreatedAt ?? d.downloadedAt),
      createdBy: userId,
      status: 'offline_cached',
    };
  });
}

type OfflineDocumentsCache = {
  [documentId: string]: OfflineDocument;
};

const OFFLINE_MANUAL_DOCUMENTS_KEY = '@tripflow_offline_manual_documents_cache_v1';

type ManualDocumentsCachePayload = {
  userId: string;
  updatedAt: number;
  documents: Record<string, unknown>[];
};

function deepSerializeDates(obj: unknown): unknown {
  if (obj == null) return obj;
  if (obj instanceof Date) return { __tfDate: obj.toISOString() };
  if (Array.isArray(obj)) return obj.map(deepSerializeDates);
  if (typeof obj === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      out[k] = deepSerializeDates(v);
    }
    return out;
  }
  return obj;
}

function deepDeserializeDates(obj: unknown): unknown {
  if (obj == null) return obj;
  if (typeof obj === 'object' && obj !== null && !Array.isArray(obj)) {
    const rec = obj as Record<string, unknown>;
    if (typeof rec.__tfDate === 'string') return new Date(rec.__tfDate);
  }
  if (Array.isArray(obj)) return obj.map(deepDeserializeDates);
  if (typeof obj === 'object' && obj !== null) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      out[k] = deepDeserializeDates(v);
    }
    return out;
  }
  return obj;
}

function serializeVaultDocumentForOfflineCache(d: Document): Record<string, unknown> {
  return {
    id: d.id,
    name: d.name,
    type: d.type,
    entryType: d.entryType,
    tripIds: d.tripIds,
    storagePath: d.storagePath ?? null,
    encryptedKey: d.encryptedKey,
    encryptionMethod: d.encryptionMethod,
    expiresOn: d.expiresOn ? d.expiresOn.toISOString() : null,
    size: d.size ?? null,
    contentType: d.contentType ?? null,
    metadata: d.metadata != null ? deepSerializeDates(d.metadata) : undefined,
    createdAt: d.createdAt.toISOString(),
    createdBy: d.createdBy,
    status: d.status,
  };
}

function deserializeVaultDocumentFromOfflineCache(raw: Record<string, unknown>): Document | null {
  try {
    const id = raw.id;
    const name = raw.name;
    const type = raw.type;
    if (typeof id !== 'string' || typeof name !== 'string' || typeof type !== 'string') return null;
    const createdAt = raw.createdAt;
    if (typeof createdAt !== 'string') return null;
    const metadataRaw = raw.metadata != null ? deepDeserializeDates(raw.metadata) : undefined;
    const entryType: Document['entryType'] = raw.entryType === 'upload' ? 'upload' : 'manual';
    return {
      id,
      name,
      type: type as Document['type'],
      entryType,
      tripIds: Array.isArray(raw.tripIds) ? raw.tripIds.filter((t): t is string => typeof t === 'string') : undefined,
      storagePath: typeof raw.storagePath === 'string' ? raw.storagePath : null,
      encryptedKey: typeof raw.encryptedKey === 'string' ? raw.encryptedKey : undefined,
      encryptionMethod: typeof raw.encryptionMethod === 'string' ? raw.encryptionMethod : undefined,
      expiresOn: typeof raw.expiresOn === 'string' ? new Date(raw.expiresOn) : null,
      size: typeof raw.size === 'number' ? raw.size : null,
      contentType: typeof raw.contentType === 'string' ? raw.contentType : null,
      metadata: metadataRaw as Document['metadata'],
      createdAt: new Date(createdAt),
      createdBy: typeof raw.createdBy === 'string' ? raw.createdBy : '',
      status: typeof raw.status === 'string' ? raw.status : 'stored',
    };
  } catch {
    return null;
  }
}

/**
 * Persist the full vault list (manual + uploads) so metadata and types stay available after cold start offline.
 * Upload rows keep Firestore `storagePath` until merged with on-device file rows from `getOfflineDocuments()`.
 */
export async function cacheVaultManualDocumentsForOffline(userId: string, documents: Document[]): Promise<void> {
  try {
    const payload: ManualDocumentsCachePayload = {
      userId,
      updatedAt: Date.now(),
      documents: documents.map(serializeVaultDocumentForOfflineCache),
    };
    await AsyncStorage.setItem(OFFLINE_MANUAL_DOCUMENTS_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error('[OfflineDocuments] Error caching vault documents for offline:', error);
  }
}

export async function getCachedOfflineManualDocuments(userId: string): Promise<Document[]> {
  try {
    const raw = await AsyncStorage.getItem(OFFLINE_MANUAL_DOCUMENTS_KEY);
    if (!raw) return [];
    const payload = JSON.parse(raw) as ManualDocumentsCachePayload;
    if (!payload?.userId || payload.userId !== userId || !Array.isArray(payload.documents)) return [];
    return payload.documents
      .map((row) => deserializeVaultDocumentFromOfflineCache(row as Record<string, unknown>))
      .filter((d): d is Document => d != null);
  } catch (error) {
    console.error('[OfflineDocuments] Error reading cached vault documents:', error);
    return [];
  }
}

export async function clearOfflineManualDocumentsCache(): Promise<void> {
  try {
    await AsyncStorage.removeItem(OFFLINE_MANUAL_DOCUMENTS_KEY);
  } catch (error) {
    console.error('[OfflineDocuments] Error clearing manual document cache:', error);
  }
}

/**
 * Ensure offline documents directory exists
 */
async function ensureOfflineDocumentsDir(): Promise<void> {
  const dirInfo = await FileSystem.getInfoAsync(OFFLINE_DOCUMENTS_DIR);
  if (!dirInfo.exists) {
    await FileSystem.makeDirectoryAsync(OFFLINE_DOCUMENTS_DIR, { intermediates: true });
  }
}

/**
 * Get all cached offline documents
 */
export async function getOfflineDocuments(): Promise<OfflineDocument[]> {
  try {
    const stored = await AsyncStorage.getItem(OFFLINE_DOCUMENTS_KEY);
    if (!stored) {
      return [];
    }
    const cache: OfflineDocumentsCache = JSON.parse(stored);
    return Object.values(cache);
  } catch (error) {
    console.error('[OfflineDocuments] Error getting offline documents:', error);
    return [];
  }
}

/**
 * Check if a document is available offline
 */
export async function isDocumentOffline(documentId: string): Promise<boolean> {
  try {
    const offlineDocs = await getOfflineDocuments();
    const doc = offlineDocs.find(d => d.documentId === documentId);
    if (!doc) {
      return false;
    }
    
    // Check if file still exists
    const fileInfo = await FileSystem.getInfoAsync(doc.localPath);
    return fileInfo.exists;
  } catch (error) {
    console.error('[OfflineDocuments] Error checking offline status:', error);
    return false;
  }
}

/**
 * Get local file path for an offline document
 */
export async function getOfflineDocumentPath(documentId: string): Promise<string | null> {
  try {
    const offlineDocs = await getOfflineDocuments();
    const doc = offlineDocs.find(d => d.documentId === documentId);
    if (!doc) {
      return null;
    }
    
    // Verify file exists
    const fileInfo = await FileSystem.getInfoAsync(doc.localPath);
    if (!fileInfo.exists) {
      // File was deleted, remove from cache
      await removeOfflineDocument(documentId);
      return null;
    }
    
    return doc.localPath;
  } catch (error) {
    console.error('[OfflineDocuments] Error getting offline path:', error);
    return null;
  }
}

/**
 * Download document for offline access
 */
export async function downloadDocumentForOffline(
  document: Document,
  downloadUrl: string
): Promise<OfflineDocument> {
  try {
    await ensureOfflineDocumentsDir();

    const extension = getOfflineStoredFileExtension(document.name, document.contentType, document.type);
    const localFileName = `${document.id}${extension}`;
    const localPath = `${OFFLINE_DOCUMENTS_DIR}${localFileName}`;

    const stored = await AsyncStorage.getItem(OFFLINE_DOCUMENTS_KEY);
    const cache: OfflineDocumentsCache = stored ? JSON.parse(stored) : {};
    const existing = cache[document.id];
    if (existing?.localPath && existing.localPath !== localPath) {
      try {
        const prevInfo = await FileSystem.getInfoAsync(existing.localPath);
        if (prevInfo.exists) {
          await FileSystem.deleteAsync(existing.localPath, { idempotent: true });
        }
      } catch {
        // best-effort: remove stale file when MIME/extension correction changes path
      }
    }

    // Download file
    const downloadResult = await FileSystem.downloadAsync(downloadUrl, localPath);
    
    if (!downloadResult.uri) {
      throw new Error('Download failed: no URI returned');
    }
    
    // Get file size
    const fileInfo = await FileSystem.getInfoAsync(localPath);
    const size = fileInfo.exists && 'size' in fileInfo ? fileInfo.size : 0;
    
    // Create offline document record
    const offlineDoc: OfflineDocument = {
      documentId: document.id,
      name: document.name,
      type: document.type,
      localPath: downloadResult.uri,
      downloadUrl,
      downloadedAt: Date.now(),
      size,
      expiresOn: document.expiresOn ? new Date(document.expiresOn).getTime() : null,
      metadata: (document.metadata ?? {}) as Record<string, unknown>,
      contentType: document.contentType ?? null,
      sourceCreatedAt: document.createdAt.getTime(),
    };

    // Save to cache
    cache[document.id] = offlineDoc;
    await AsyncStorage.setItem(OFFLINE_DOCUMENTS_KEY, JSON.stringify(cache));
    
    console.log(`[OfflineDocuments] Downloaded document: ${document.name} (${(size / 1024).toFixed(2)} KB)`);
    
    return offlineDoc;
  } catch (error) {
    console.error('[OfflineDocuments] Error downloading document:', error);
    throw new Error(`Failed to download document: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Remove document from offline cache
 */
export async function removeOfflineDocument(documentId: string): Promise<void> {
  try {
    // Get cached documents
    const stored = await AsyncStorage.getItem(OFFLINE_DOCUMENTS_KEY);
    if (!stored) {
      return;
    }
    
    const cache: OfflineDocumentsCache = JSON.parse(stored);
    const doc = cache[documentId];
    
    if (doc) {
      // Delete file
      try {
        const fileInfo = await FileSystem.getInfoAsync(doc.localPath);
        if (fileInfo.exists) {
          await FileSystem.deleteAsync(doc.localPath, { idempotent: true });
        }
      } catch (fileError) {
        console.warn('[OfflineDocuments] Error deleting file:', fileError);
      }
      
      // Remove from cache
      delete cache[documentId];
      await AsyncStorage.setItem(OFFLINE_DOCUMENTS_KEY, JSON.stringify(cache));
      
      console.log(`[OfflineDocuments] Removed offline document: ${documentId}`);
    }
  } catch (error) {
    console.error('[OfflineDocuments] Error removing offline document:', error);
  }
}

/**
 * Drop offline index entries whose files are missing (e.g. OS cleared cache).
 * Returns documents that still have a file on disk.
 */
export async function pruneOfflineDocumentsWithMissingFiles(): Promise<OfflineDocument[]> {
  const all = await getOfflineDocuments();
  const kept: OfflineDocument[] = [];
  for (const d of all) {
    try {
      const info = await FileSystem.getInfoAsync(d.localPath);
      if (info.exists) {
        kept.push(d);
      } else {
        await removeOfflineDocument(d.documentId);
      }
    } catch {
      try {
        await removeOfflineDocument(d.documentId);
      } catch {
        // ignore
      }
    }
  }
  return kept;
}

/**
 * Clear all offline documents
 */
export async function clearAllOfflineDocuments(): Promise<void> {
  try {
    const offlineDocs = await getOfflineDocuments();
    
    // Delete all files
    for (const doc of offlineDocs) {
      try {
        const fileInfo = await FileSystem.getInfoAsync(doc.localPath);
        if (fileInfo.exists) {
          await FileSystem.deleteAsync(doc.localPath, { idempotent: true });
        }
      } catch (fileError) {
        console.warn('[OfflineDocuments] Error deleting file:', fileError);
      }
    }
    
    // Clear cache
    await AsyncStorage.multiRemove([
      OFFLINE_DOCUMENTS_KEY,
      TRIP_DOCUMENTS_KEY,
      OFFLINE_MANUAL_DOCUMENTS_KEY,
    ]);

    try {
      const dirInfo = await FileSystem.getInfoAsync(OFFLINE_DOCUMENTS_DIR);
      if (dirInfo.exists) {
        await FileSystem.deleteAsync(OFFLINE_DOCUMENTS_DIR, { idempotent: true });
      }
    } catch (dirError) {
      console.warn('[OfflineDocuments] Error removing offline documents directory:', dirError);
    }

    console.log('[OfflineDocuments] Cleared all offline documents');
  } catch (error) {
    console.error('[OfflineDocuments] Error clearing offline documents:', error);
  }
}

/**
 * Get total size of all offline documents
 */
export async function getOfflineDocumentsSize(): Promise<number> {
  try {
    const offlineDocs = await getOfflineDocuments();
    return offlineDocs.reduce((total, doc) => total + doc.size, 0);
  } catch (error) {
    console.error('[OfflineDocuments] Error calculating size:', error);
    return 0;
  }
}

/**
 * Clean up expired documents
 */
export async function cleanupExpiredDocuments(): Promise<number> {
  try {
    const offlineDocs = await getOfflineDocuments();
    const now = Date.now();
    let cleanedCount = 0;
    
    for (const doc of offlineDocs) {
      if (doc.expiresOn && doc.expiresOn < now) {
        await removeOfflineDocument(doc.documentId);
        cleanedCount++;
      }
    }
    
    if (cleanedCount > 0) {
      console.log(`[OfflineDocuments] Cleaned up ${cleanedCount} expired documents`);
    }
    
    return cleanedCount;
  } catch (error) {
    console.error('[OfflineDocuments] Error cleaning up expired documents:', error);
    return 0;
  }
}

/**
 * Extension for the offline file on disk. Uses real MIME (`contentType`) — vault categories
 * like `passport` are not MIME types; defaulting them to `.pdf` broke JPEG uploads without
 * a filename extension (opened in the PDF viewer, "View attachment" appeared to do nothing).
 */
function getOfflineStoredFileExtension(
  name: string,
  contentType: string | null | undefined,
  documentType: string,
): string {
  const nameMatch = name.match(/\.([^.]+)$/i);
  if (nameMatch) {
    return `.${nameMatch[1].toLowerCase()}`;
  }

  const typeMap: Record<string, string> = {
    'application/pdf': '.pdf',
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/png': '.png',
    'image/webp': '.webp',
    'image/gif': '.gif',
    'image/heic': '.heic',
    'image/heif': '.heif',
  };

  const ct = (contentType || '').toLowerCase().split(';')[0].trim();
  if (ct && typeMap[ct]) {
    return typeMap[ct];
  }

  const dt = documentType.toLowerCase();
  if (dt.includes('/') && typeMap[dt]) {
    return typeMap[dt];
  }

  return '.bin';
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
}

/**
 * Download every file-backed vault document that is not already stored locally.
 * Used from Documents and Offline settings.
 */
export async function downloadAllPendingFileDocumentsForOffline(
  documents: Document[],
  onProgress?: (done: number, total: number) => void,
): Promise<{ success: number; failed: number; pending: number; downloadedIds: string[] }> {
  if (!networkMonitor.isOnline()) {
    return { success: 0, failed: 0, pending: 0, downloadedIds: [] };
  }

  const pending: Document[] = [];
  for (const d of documents) {
    if (d.entryType === 'manual') continue;
    if (await isDocumentOffline(d.id)) continue;
    pending.push(d);
  }

  if (pending.length === 0) {
    return { success: 0, failed: 0, pending: 0, downloadedIds: [] };
  }

  let success = 0;
  let failed = 0;
  const downloadedIds: string[] = [];

  for (let i = 0; i < pending.length; i++) {
    const document = pending[i];
    try {
      const docWithUrl = await getDocumentWithDownloadUrl(document.id);
      if (docWithUrl.downloadUrl) {
        await downloadDocumentForOffline(document, docWithUrl.downloadUrl);
        success++;
        downloadedIds.push(document.id);
      } else {
        failed++;
      }
    } catch (error) {
      console.error(`[OfflineDocuments] Bulk download failed for ${document.name}:`, error);
      failed++;
    }
    onProgress?.(i + 1, pending.length);
  }

  return { success, failed, pending: pending.length, downloadedIds };
}

/**
 * Download all documents for a trip
 * Auto-downloads when trip becomes active (within 7 days)
 */
export async function downloadDocumentsForTrip(
  tripId: string,
  documents: Document[],
  onProgress?: (progress: { downloaded: number; total: number }) => void
): Promise<{ success: number; failed: number }> {
  try {
    if (!networkMonitor.isOnline()) {
      console.warn('[OfflineDocuments] Cannot download documents: device is offline');
      return { success: 0, failed: documents.length };
    }

    let success = 0;
    let failed = 0;

    for (let i = 0; i < documents.length; i++) {
      const document = documents[i];
      
      try {
        // Check if already downloaded
        const isOffline = await isDocumentOffline(document.id);
        if (isOffline) {
          console.log(`[OfflineDocuments] Document ${document.name} already offline, skipping`);
          success++;
          continue;
        }

        // Get download URL
        const docWithUrl = await getDocumentWithDownloadUrl(document.id);
        if (!docWithUrl.downloadUrl) {
          console.warn(`[OfflineDocuments] No download URL for document ${document.name}`);
          failed++;
          continue;
        }

        // Download document
        await downloadDocumentForOffline(document, docWithUrl.downloadUrl);
        
        // Associate with trip
        await associateDocumentWithTrip(document.id, tripId);
        
        success++;
        
        // Report progress
        if (onProgress) {
          onProgress({ downloaded: i + 1, total: documents.length });
        }
      } catch (error) {
        console.error(`[OfflineDocuments] Error downloading document ${document.name}:`, error);
        failed++;
      }
    }

    console.log(`[OfflineDocuments] Downloaded ${success}/${documents.length} documents for trip ${tripId}`);
    return { success, failed };
  } catch (error) {
    console.error(`[OfflineDocuments] Error downloading documents for trip ${tripId}:`, error);
    return { success: 0, failed: documents.length };
  }
}

/**
 * Associate a document with a trip
 */
async function associateDocumentWithTrip(documentId: string, tripId: string): Promise<void> {
  try {
    const stored = await AsyncStorage.getItem(TRIP_DOCUMENTS_KEY);
    const tripDocuments: Record<string, string[]> = stored ? JSON.parse(stored) : {};
    
    if (!tripDocuments[tripId]) {
      tripDocuments[tripId] = [];
    }
    
    if (!tripDocuments[tripId].includes(documentId)) {
      tripDocuments[tripId].push(documentId);
    }
    
    await AsyncStorage.setItem(TRIP_DOCUMENTS_KEY, JSON.stringify(tripDocuments));
  } catch (error) {
    console.error('[OfflineDocuments] Error associating document with trip:', error);
  }
}

/**
 * Get document IDs for a trip
 */
export async function getTripDocumentIds(tripId: string): Promise<string[]> {
  try {
    const stored = await AsyncStorage.getItem(TRIP_DOCUMENTS_KEY);
    const tripDocuments: Record<string, string[]> = stored ? JSON.parse(stored) : {};
    return tripDocuments[tripId] || [];
  } catch (error) {
    console.error('[OfflineDocuments] Error getting trip document IDs:', error);
    return [];
  }
}

/**
 * Get offline documents for a trip
 */
export async function getOfflineDocumentsForTrip(tripId: string): Promise<OfflineDocument[]> {
  try {
    const documentIds = await getTripDocumentIds(tripId);
    const allOfflineDocs = await getOfflineDocuments();
    
    return allOfflineDocs.filter(doc => documentIds.includes(doc.documentId));
  } catch (error) {
    console.error('[OfflineDocuments] Error getting offline documents for trip:', error);
    return [];
  }
}

/**
 * Remove all documents for a trip from offline cache
 */
export async function removeTripDocuments(tripId: string): Promise<void> {
  try {
    const documentIds = await getTripDocumentIds(tripId);
    
    for (const documentId of documentIds) {
      await removeOfflineDocument(documentId);
    }
    
    // Remove trip association
    const stored = await AsyncStorage.getItem(TRIP_DOCUMENTS_KEY);
    const tripDocuments: Record<string, string[]> = stored ? JSON.parse(stored) : {};
    delete tripDocuments[tripId];
    await AsyncStorage.setItem(TRIP_DOCUMENTS_KEY, JSON.stringify(tripDocuments));
    
    console.log(`[OfflineDocuments] Removed ${documentIds.length} documents for trip ${tripId}`);
  } catch (error) {
    console.error('[OfflineDocuments] Error removing trip documents:', error);
  }
}

/**
 * Auto-download documents for active trips
 * Should be called when a trip becomes active (within 7 days of start date)
 */
export async function autoDownloadForActiveTrip(
  tripId: string,
  tripStartDate: Date,
  documents: Document[]
): Promise<void> {
  try {
    if (!isWithin7Days(tripStartDate)) {
      console.log(`[OfflineDocuments] Trip ${tripId} is not active yet, skipping auto-download`);
      return;
    }

    if (documents.length === 0) {
      console.log(`[OfflineDocuments] No documents to download for trip ${tripId}`);
      return;
    }

    console.log(`[OfflineDocuments] Auto-downloading ${documents.length} documents for active trip ${tripId}`);
    
    await downloadDocumentsForTrip(tripId, documents, (progress) => {
      console.log(`[OfflineDocuments] Progress: ${progress.downloaded}/${progress.total}`);
    });
  } catch (error) {
    console.error(`[OfflineDocuments] Error auto-downloading for trip ${tripId}:`, error);
  }
}

/**
 * Get storage statistics
 */
export async function getStorageStats(): Promise<{
  totalSize: number;
  documentCount: number;
  tripCount: number;
  byTrip: Record<string, { count: number; size: number }>;
}> {
  try {
    const allDocs = await getOfflineDocuments();
    const totalSize = allDocs.reduce((sum, doc) => sum + doc.size, 0);
    
    const stored = await AsyncStorage.getItem(TRIP_DOCUMENTS_KEY);
    const tripDocuments: Record<string, string[]> = stored ? JSON.parse(stored) : {};
    
    const byTrip: Record<string, { count: number; size: number }> = {};
    
    for (const [tripId, documentIds] of Object.entries(tripDocuments)) {
      const tripDocs = allDocs.filter(doc => documentIds.includes(doc.documentId));
      byTrip[tripId] = {
        count: tripDocs.length,
        size: tripDocs.reduce((sum, doc) => sum + doc.size, 0),
      };
    }
    
    return {
      totalSize,
      documentCount: allDocs.length,
      tripCount: Object.keys(tripDocuments).length,
      byTrip,
    };
  } catch (error) {
    console.error('[OfflineDocuments] Error getting storage stats:', error);
    return {
      totalSize: 0,
      documentCount: 0,
      tripCount: 0,
      byTrip: {},
    };
  }
}

/**
 * Clean up old trip documents (after trip + 30 days)
 */
export async function cleanupOldTripDocuments(
  activeTripIds: string[]
): Promise<number> {
  try {
    const stored = await AsyncStorage.getItem(TRIP_DOCUMENTS_KEY);
    const tripDocuments: Record<string, string[]> = stored ? JSON.parse(stored) : {};
    
    let cleanedCount = 0;
    const tripsToRemove: string[] = [];
    
    for (const tripId of Object.keys(tripDocuments)) {
      if (!activeTripIds.includes(tripId)) {
        // Trip is no longer active, remove its documents
        const documentIds = tripDocuments[tripId];
        for (const documentId of documentIds) {
          await removeOfflineDocument(documentId);
          cleanedCount++;
        }
        tripsToRemove.push(tripId);
      }
    }
    
    // Remove trip associations
    for (const tripId of tripsToRemove) {
      delete tripDocuments[tripId];
    }
    
    if (tripsToRemove.length > 0) {
      await AsyncStorage.setItem(TRIP_DOCUMENTS_KEY, JSON.stringify(tripDocuments));
      console.log(`[OfflineDocuments] Cleaned up ${cleanedCount} documents from ${tripsToRemove.length} old trips`);
    }
    
    return cleanedCount;
  } catch (error) {
    console.error('[OfflineDocuments] Error cleaning up old trip documents:', error);
    return 0;
  }
}

